import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { applyLocale, I18n, loadLocale, saveLocale, type Locale } from './i18n';

interface Ctx {
  i18n: I18n;
  /** `i18n.t` 绑好的版本。**身份稳定** —— 见 useI18n 的说明。 */
  t: I18n['t'];
  locale: Locale;
  setLocale(l: Locale): void;
}

const I18nCtx = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // 初值同步读，**不要放在 effect 里** —— 否则先用默认语言画一帧再闪一下换掉
  const [locale, setLocale] = useState<Locale>(() =>
    loadLocale(typeof localStorage === 'undefined' ? null : localStorage,
               typeof navigator === 'undefined' ? [] : navigator.languages ?? []));

  useEffect(() => {
    applyLocale(document.documentElement, locale);
    saveLocale(typeof localStorage === 'undefined' ? null : localStorage, locale);
  }, [locale]);

  // **t 必须和 i18n 一起 memo。**
  //
  // 原来 t 是在 useI18n 里 `i18n.t.bind(i18n)` 现算的 —— 那样每次渲染都是一个
  // 新函数，于是任何 `useEffect(..., [t])` 都会每帧重跑一次。effect 里再 setState
  // 的话就成了闭环：拉数据 → 渲染 → 依赖变了 → 再拉。
  //
  // 实测个人页因为这个在后台每秒打几十个请求（浏览器网络面板里 169 次
  // /payout/identity 才让它露出来）。**它不会报错，只会烧服务器**，
  // 而且本地开发时一切看着都正常。
  const value = useMemo<Ctx>(() => {
    const i18n = new I18n(locale);
    return { i18n, t: i18n.t.bind(i18n), locale, setLocale };
  }, [locale]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

/**
 * `const { t } = useI18n()` —— 组件里就这一个入口。
 *
 * **直接把 context 原样返回**，不在这里 `{...c}` 展开、也不在这里 bind ——
 * 那两件事都会每次渲染造出新对象/新函数，让 `[t]` 这种依赖数组永远"变了"。
 */
export function useI18n(): Ctx {
  const c = useContext(I18nCtx);
  if (!c) throw new Error('useI18n 必须在 I18nProvider 里用');
  return c;
}
